import React, { useEffect, useState } from "react";
import { ShieldCheck, Clock, XCircle, MinusCircle, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

// Verification BREAKDOWN — replaces the single-number verification badge.
// Design principle from the founder: employers should immediately see WHAT has
// been verified, not try to interpret a number.

const STATE_META = {
    verified: {
        icon: ShieldCheck,
        label: "Verified",
        tone: "text-emerald-600 dark:text-emerald-400",
        chip: "bg-emerald-500/15 border-emerald-500/30",
    },
    in_progress: {
        icon: Clock,
        label: "In review",
        tone: "text-amber-600 dark:text-amber-400",
        chip: "bg-amber-500/15 border-amber-500/30",
    },
    review_required: {
        icon: AlertTriangle,
        label: "Review required",
        tone: "text-amber-700 dark:text-amber-300",
        chip: "bg-amber-500/15 border-amber-500/30",
    },
    failed: {
        icon: XCircle,
        label: "Failed",
        tone: "text-destructive",
        chip: "bg-destructive/10 border-destructive/30",
    },
    expired: {
        icon: XCircle,
        label: "Expired",
        tone: "text-muted-foreground",
        chip: "bg-muted border-border",
    },
    not_requested: {
        icon: MinusCircle,
        label: "Not requested",
        tone: "text-muted-foreground/70",
        chip: "bg-muted/50 border-border",
    },
    not_available: {
        icon: MinusCircle,
        label: "Coming soon",
        tone: "text-muted-foreground/70",
        chip: "bg-muted/50 border-border",
    },
    not_applicable: {
        icon: MinusCircle,
        label: "Not applicable",
        tone: "text-muted-foreground/70",
        chip: "bg-muted/50 border-border",
    },
};

const TrustTile = ({ tile }) => {
    const meta = STATE_META[tile.state] || STATE_META.not_requested;
    const Icon = meta.icon;
    return (
        <div
            className={`flex items-center justify-between gap-3 rounded-xl border ${meta.chip} px-3 py-2`}
            data-testid={`trust-tile-${tile.kind}`}
        >
            <div className="min-w-0">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{tile.label}</div>
                <div className={`text-sm font-semibold ${meta.tone} truncate`}>{meta.label}</div>
            </div>
            <Icon size={16} className={`${meta.tone} shrink-0`} />
        </div>
    );
};

/**
 * TrustBreakdownCard
 * @param {string} email — the talent's email (source of truth for lookup).
 * @param {string} [size] — "compact" for tables/marketplaces; "full" (default) for detail pages.
 * @param {object} [initialData] — pre-fetched breakdown (skip network).
 */
export const TrustBreakdownCard = ({ email, size = "full", initialData = null }) => {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(!initialData);

    useEffect(() => {
        if (initialData || !email) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/verify/trust-breakdown/${encodeURIComponent(email)}`);
                if (!cancelled) setData(data);
            } catch {
                if (!cancelled) setData({ tiles: [], verified_count: 0, active_count: 0 });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [email, initialData]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-border p-4 animate-pulse" data-testid="trust-breakdown-loading">
                <div className="h-3 w-32 bg-muted rounded" />
                <div className="mt-3 grid grid-cols-2 gap-2">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-10 rounded-xl bg-muted" />)}
                </div>
            </div>
        );
    }

    const tiles = data?.tiles || [];
    const verifiedCount = data?.verified_count || 0;

    if (size === "compact") {
        // Compact horizontal strip for table rows / cards — only surface tiles
        // that carry a real signal (verified / in-progress / failed). Hide
        // "not_requested" so the strip stays scannable.
        const visible = tiles.filter((t) => !["not_applicable", "not_available", "not_requested"].includes(t.state));
        if (visible.length === 0) {
            return (
                <span
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
                    data-testid="trust-breakdown-compact-empty"
                    title="No verifications requested yet"
                >
                    <ShieldCheck size={10} /> Not yet verified
                </span>
            );
        }
        return (
            <div className="inline-flex flex-wrap items-center gap-1" data-testid="trust-breakdown-compact">
                {visible.map((t) => {
                    const meta = STATE_META[t.state] || STATE_META.not_requested;
                    const Icon = meta.icon;
                    return (
                        <span
                            key={t.kind}
                            title={`${t.label}: ${meta.label}`}
                            className={`inline-flex items-center gap-1 rounded-full border ${meta.chip} px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}
                            data-testid={`trust-compact-${t.kind}`}
                        >
                            <Icon size={10} /> {t.label.split(" ")[0]}
                        </span>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="trust-breakdown-card">
            <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                    <div className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Verified Talent Passport™</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Powered by Simonara Verify™</div>
                </div>
                <div className="text-right">
                    <div className="font-heading font-bold text-lg text-foreground mono-score" data-testid="trust-verified-count">
                        {verifiedCount}<span className="text-muted-foreground text-sm">/{data?.active_count || 4}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Verified</div>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tiles.map((t) => <TrustTile key={t.kind} tile={t} />)}
            </div>
        </div>
    );
};

export default TrustBreakdownCard;
